<template>
  <b-modal
    id="notification-modal"
    v-model="visible"
    title="站内通知"
    size="lg"
    hide-footer
    centered
    dialog-class="notification-modal-dialog"
    content-class="notification-modal-content"
    header-class="notification-modal-header"
    body-class="notification-modal-body p-0"
    @show="onModalShow"
  >
    <!-- 顶部状态栏与操作 -->
    <div class="notify-toolbar d-flex justify-content-between align-items-center px-3 py-2 border-bottom">
      <div class="notify-tabs btn-group btn-group-sm">
        <button 
          type="button" 
          class="btn" 
          :class="activeFilter === 'all' ? 'btn-primary' : 'btn-outline-secondary'"
          @click="setFilter('all')"
        >
          全部
          <span v-if="total > 0" class="small">({{ total }})</span>
        </button>
        <button 
          type="button" 
          class="btn" 
          :class="activeFilter === 'unread' ? 'btn-primary' : 'btn-outline-secondary'"
          @click="setFilter('unread')"
        >
          未读
          <span v-if="unreadCount > 0" class="badge badge-danger ml-1">{{ unreadCount }}</span>
        </button>
      </div>

      <div class="notify-actions">
        <b-button 
          v-if="unreadCount > 0" 
          variant="link" 
          size="sm" 
          class="text-decoration-none text-muted p-0 read-all-btn"
          @click="markAllAsRead"
          :disabled="loading"
        >
          <b-icon icon="check2-all" class="mr-1"></b-icon>全部标为已读
        </b-button>
      </div>
    </div>

    <!-- 列表内容区域 -->
    <div class="notify-content-wrap position-relative">
      <!-- 加载中遮罩 -->
      <div v-if="loading" class="text-center py-5">
        <b-spinner variant="primary" small></b-spinner>
        <div class="small text-muted mt-2">加载通知中...</div>
      </div>

      <!-- 空状态 -->
      <div v-else-if="items.length === 0" class="empty-state text-center py-5">
        <b-icon icon="inbox" font-scale="3" class="text-muted mb-3 opacity-50"></b-icon>
        <div class="text-muted">{{ activeFilter === 'unread' ? '暂无未读通知' : '暂无任何通知' }}</div>
      </div>

      <!-- 消息列表 -->
      <div v-else class="notify-list">
        <div 
          v-for="item in items" 
          :key="item.id"
          class="notify-item px-3 py-3 border-bottom transition-all"
          :class="{ 'is-unread': item.is_read === 0 }"
          @click="handleItemClick(item)"
        >
          <div class="d-flex justify-content-between align-items-start mb-1">
            <div class="notify-title-wrap d-flex align-items-center flex-wrap">
              <!-- 未读红点指示 -->
              <span v-if="item.is_read === 0" class="unread-dot mr-2"></span>
              
              <!-- 类型标签 -->
              <span 
                class="badge badge-pill mr-2 notify-badge"
                :class="getTypeBadgeClass(item.type)"
              >
                {{ getTypeLabel(item.type) }}
              </span>

              <h6 class="notify-title mb-0 font-weight-bold" :class="item.is_read === 0 ? 'text-main' : 'text-muted'">
                {{ item.title }}
              </h6>
            </div>

            <small class="text-muted ml-2 flex-shrink-0 notify-time">
              {{ formatTime(item.created_at) }}
            </small>
          </div>

          <!-- 正文内容 -->
          <div class="notify-body text-secondary mt-2 mb-2">
            {{ item.content }}
          </div>

          <!-- 底部操作与跳转 -->
          <div v-if="item.link" class="notify-footer mt-2 text-right">
            <b-button 
              size="sm" 
              variant="outline-primary" 
              class="py-0 px-2 font-size-xs"
              @click.stop="navigateLink(item)"
            >
              前往查看 <b-icon icon="chevron-right" font-scale="0.8"></b-icon>
            </b-button>
          </div>
        </div>
      </div>

      <!-- 分页控件 -->
      <div v-if="total > pageSize" class="p-2 border-top d-flex justify-content-center">
        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="pageSize"
          size="sm"
          class="mb-0"
          @change="onPageChange"
        ></b-pagination>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { request_zzquant } from '@/network/request.js'

export default {
  name: 'NotificationModal',
  data() {
    return {
      visible: false,
      loading: false,
      activeFilter: 'all', // 'all' | 'unread'
      items: [],
      total: 0,
      unreadCount: 0,
      page: 1,
      pageSize: 15,
      pollTimer: null,
    }
  },
  mounted() {
    this.startPolling()
    this.loginHandler = () => {
      this.startPolling()
    }
    this.logoutHandler = () => {
      this.stopPolling()
      this.unreadCount = 0
      this.$emit('unread-changed', 0)
    }
    window.addEventListener('user-login', this.loginHandler)
    window.addEventListener('user-logout', this.logoutHandler)
  },
  beforeDestroy() {
    this.stopPolling()
    if (this.loginHandler) {
      window.removeEventListener('user-login', this.loginHandler)
    }
    if (this.logoutHandler) {
      window.removeEventListener('user-logout', this.logoutHandler)
    }
  },
  methods: {
    startPolling() {
      this.stopPolling()
      const token = localStorage.getItem('token')
      if (!token) return
      this.fetchUnreadCount()
      this.pollTimer = setInterval(() => {
        this.fetchUnreadCount()
      }, 60000)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    open() {
      this.visible = true
    },
    close() {
      this.visible = false
    },
    onModalShow() {
      this.page = 1
      this.loadNotifications()
      this.fetchUnreadCount()
    },
    setFilter(filter) {
      this.activeFilter = filter
      this.page = 1
      this.loadNotifications()
    },
    onPageChange(newPage) {
      this.page = newPage
      this.loadNotifications()
    },
    fetchUnreadCount() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.stopPolling()
        return
      }

      request_zzquant({
        url: '/v3/notifications/unread-count',
        method: 'GET',
        noRedirect: true,
      }).then(res => {
        if (res && res.data && res.data.code === 20000) {
          this.unreadCount = res.data.data.unread_count || 0
          this.$emit('unread-changed', this.unreadCount)
        }
      }).catch(err => {
        // 若 401（未登录或 Token 失效/过期），停止轮询定时器，防止死循环刷接口
        const status = (err && err.response && err.response.status) || (err && err.status)
        if (status === 401) {
          this.stopPolling()
        }
      })
    },
    loadNotifications() {
      const token = localStorage.getItem('token')
      if (!token) return

      this.loading = true
      const params = {
        page: this.page,
        page_size: this.pageSize,
      }
      if (this.activeFilter === 'unread') {
        params.is_read = 0
      }

      request_zzquant({
        url: '/v3/notifications',
        method: 'GET',
        params,
      }).then(res => {
        this.loading = false
        if (res && res.data && res.data.code === 20000) {
          const data = res.data.data || {}
          this.items = data.items || []
          this.total = data.total || 0
          this.unreadCount = data.unread_count || 0
          this.$emit('unread-changed', this.unreadCount)
        }
      }).catch(err => {
        this.loading = false
        console.warn('Load notifications failed', err)
      })
    },
    handleItemClick(item) {
      if (item.is_read === 0) {
        this.markItemRead(item)
      }
    },
    markItemRead(item) {
      request_zzquant({
        url: '/v3/notifications/read',
        method: 'POST',
        data: { ids: [item.id] },
      }).then(res => {
        if (res && res.data && res.data.code === 20000) {
          item.is_read = 1
          if (this.unreadCount > 0) {
            this.unreadCount--
            this.$emit('unread-changed', this.unreadCount)
          }
          if (this.activeFilter === 'unread') {
            this.items = this.items.filter(i => i.id !== item.id)
            this.total = Math.max(0, this.total - 1)
          }
        }
      }).catch(err => console.warn('mark read failed', err))
    },
    markAllAsRead() {
      request_zzquant({
        url: '/v3/notifications/read-all',
        method: 'POST',
      }).then(res => {
        if (res && res.data && res.data.code === 20000) {
          this.items.forEach(i => { i.is_read = 1 })
          this.unreadCount = 0
          this.$emit('unread-changed', 0)
          if (this.activeFilter === 'unread') {
            this.items = []
            this.total = 0
          }
        }
      }).catch(err => console.warn('mark all read failed', err))
    },
    navigateLink(item) {
      if (item.is_read === 0) {
        this.markItemRead(item)
      }
      this.close()
      if (item.link) {
        if (item.link.startsWith('http://') || item.link.startsWith('https://')) {
          window.open(item.link, '_blank')
        } else {
          try {
            this.$router.push(item.link)
          } catch (e) {
            console.warn('Navigation failed', e)
          }
        }
      }
    },
    getTypeLabel(type) {
      const map = {
        vip: 'VIP权益',
        system: '系统通知',
        activity: '活动公告',
        topic: '题材提醒',
      }
      return map[type] || '通知'
    },
    getTypeBadgeClass(type) {
      const map = {
        vip: 'badge-warning text-dark font-weight-bold',
        system: 'badge-primary',
        activity: 'badge-success',
        topic: 'badge-info',
      }
      return map[type] || 'badge-secondary'
    },
    formatTime(timeStr) {
      if (!timeStr) return ''
      return timeStr.substring(0, 16)
    },
  },
}
</script>

<style scoped>
.notification-modal-content {
  background-color: var(--bg-card, #ffffff);
  color: var(--text-main, #1e293b);
  border-radius: 8px;
  overflow: hidden;
}

.notify-toolbar {
  background-color: var(--bg-muted, #f8fafc);
}

.notify-list {
  max-height: 480px;
  overflow-y: auto;
}

.notify-item {
  cursor: pointer;
  background-color: var(--bg-card, #ffffff);
  transition: background-color 0.2s;
}

.notify-item:hover {
  background-color: var(--bg-muted, #f1f5f9);
}

.notify-item.is-unread {
  background-color: rgba(59, 130, 246, 0.04);
}

body.theme-dark .notify-item.is-unread {
  background-color: rgba(59, 130, 246, 0.12);
}

.unread-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ef4444;
  display: inline-block;
}

.notify-badge {
  font-size: 0.72rem;
  padding: 0.25em 0.6em;
}

.notify-title {
  font-size: 0.95rem;
}

.notify-body {
  font-size: 0.86rem;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
}

.notify-time {
  font-size: 0.75rem;
}

.font-size-xs {
  font-size: 0.75rem;
}

.read-all-btn:hover {
  color: var(--accent-color, #3b82f6) !important;
}
</style>
